import { useState } from "react";
import { currency, percent } from "../../lib/formatting";
import { propertyMetrics, propertyHoldingStatus, interEntityLoanInterest, grossYield, netYield, isSMSF, totalLoanBalance, portfolioSummary } from "../../lib/calculations";
import type { PropertyRecord } from "../../types/portfolio";
import type { Structure, InvestorProfile } from "../../types/entities";
import type { SubscriptionTier } from "../billing/BillingPage";
import { InfoTooltip } from "../ui/InfoTooltip";
import { NumberInput } from "../ui/NumberInput";

function isCompanyEntity(entityType: string | undefined): boolean {
  if (!entityType) return false;
  return entityType.includes("Company") || entityType.includes("Pty Ltd");
}

function companyAdjustedCashflow(rawCashflow: number, entityType: string | undefined): number {
  return isCompanyEntity(entityType) && rawCashflow > 0 ? rawCashflow * 0.7 : rawCashflow;
}

/**
 * Canonical entity cashflow formula (Day 37 — number consistency rule).
 *
 * Returns the entity's NET monthly cashflow after:
 *   - per-property ownership adjustment (Day 36)
 *   - companyAdjustedCashflow (30% company tax for Company entities)
 *   - subtracting inter-entity formal-loan interest obligations
 *   - netting positive properties against negative ones
 *
 * This is the SINGLE SOURCE OF TRUTH for the entity's cashflow position.
 * All three pills on the entity card (Cashflow/fn, Top-Up, Self-funding
 * badge) derive from this one number. Pre-Day-37 each pill had its own
 * independent reduce with different adjustments and they could disagree.
 *
 * Per ARCHITECTURE §7 "Number consistency rule".
 */
function computeEntityCashflow(
  entityProps: PropertyRecord[],
  structure: { entityType: string },
  allCommittedProps: PropertyRecord[],
  structureId: string
): number {
  const propertyContributions = entityProps.reduce(
    (sum, p) => sum + companyAdjustedCashflow(propertyMetrics(p).netCashflowShare, structure.entityType),
    0
  );
  const interEntity = interEntityLoanInterest(allCommittedProps, structureId);
  return propertyContributions - interEntity;
}

export default function PortfolioPage({
  portfolioProperties,
  portfolioStructures,
  profile: _profile,
  subscriptionTier: _subscriptionTier,
  onDeleteDraft,
  onConfirmDraft,
  onUpdateDraft: _onUpdateDraft,
  onSelectProperty,
  onGoToSimulator,
  onNavigateToProfile,
  onOpenImport,
  onAddProperty,
  onNavigateToBilling,
  onUpdateStructure,
  includeSMSFInPortfolio = true,
  hasFullAccess = false,
}: {
  portfolioProperties: PropertyRecord[];
  portfolioStructures: Structure[];
  profile?: InvestorProfile;
  subscriptionTier?: SubscriptionTier;
  onDeleteDraft?: (propertyId: string) => void;
  onConfirmDraft?: (propertyId: string) => void;
  onUpdateDraft?: (property: PropertyRecord) => void;
  onSelectProperty?: (propertyId: string) => void;
  onGoToSimulator?: () => void;
  onNavigateToProfile?: () => void;
  onOpenImport?: () => void;
  onAddProperty?: () => void;
  onNavigateToBilling?: () => void;
  onUpdateStructure?: (structure: Structure) => void;
  includeSMSFInPortfolio?: boolean;
  hasFullAccess?: boolean;
}) {
  const [structuresPanelOpen, setStructuresPanelOpen] = useState(false);
  const [portfolioTab, setPortfolioTab] = useState<"active" | "history">("active");
  const [confirmModal, setConfirmModal] = useState<{
    propertyId: string;
    nickname: string;
    address: string;
    purchasePrice: number;
    estimatedCosts: number;
  } | null>(null);

  const committedProperties = portfolioProperties.filter(
    (p) => !p.notes?.includes("[DRAFT SCENARIO]") && (!p.status || p.status === "active") && p.address?.trim()
  );

  const historicalProperties = portfolioProperties.filter(
    (p) => !p.notes?.includes("[DRAFT SCENARIO]") && (p.status === "sold" || p.status === "historical")
  );

  const draftProperties = portfolioProperties.filter((p) =>
    p.notes?.includes("[DRAFT SCENARIO]")
  );

  return (
    <div className="space-y-5 md:space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900">Portfolio</h1>
          <p className="mt-2 text-neutral-600">
            Grouped by entity first, because that is how portfolio cashflow is actually managed.
          </p>
        </div>
        {onAddProperty && (
          <button
            onClick={onAddProperty}
            className="shrink-0 flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add property
          </button>
        )}
        {onOpenImport && (
          hasFullAccess ? (
            <button
              onClick={onOpenImport}
              className="shrink-0 flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Import properties
            </button>
          ) : (
            <button
              onClick={onNavigateToBilling}
              className="shrink-0 flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-400 hover:bg-neutral-50 transition-colors shadow-sm"
              title="Import wizard is available on the Investor plan"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Import properties
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Investor</span>
            </button>
          )
        )}
      </div>

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className="flex gap-0 border-b border-neutral-200">
        {(["active", "history"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setPortfolioTab(tab)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              portfolioTab === tab
                ? "border-[#C47A2F] text-[#C47A2F]"
                : "border-transparent text-neutral-500 hover:text-neutral-700"
            }`}
          >
            {tab === "active" ? (
              <>Active <span className="ml-1.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[11px] font-semibold text-neutral-500">{committedProperties.length}</span></>
            ) : (
              <>History {historicalProperties.length > 0 && <span className="ml-1.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[11px] font-semibold text-neutral-500">{historicalProperties.length}</span>}</>
            )}
          </button>
        ))}
      </div>

      {portfolioTab === "active" && (<>

      {/* ── Free tier limit banner ──────────────────────────────────────── */}
      {!hasFullAccess && committedProperties.length >= 2 && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Subscribe to add more properties.</span>{" "}
            You have {committedProperties.length} {committedProperties.length === 1 ? "property" : "properties"}.
            Subscribe to add unlimited properties.
          </p>
          <button
            onClick={onNavigateToBilling}
            className="w-full rounded-lg bg-neutral-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-neutral-700 transition-colors sm:w-auto"
          >
            Upgrade to Investor
          </button>
        </div>
      )}

      {/* ── Structures panel ────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-neutral-200 bg-neutral-50">
        <button
          onClick={() => setStructuresPanelOpen((o) => !o)}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
              Structures
            </span>
            <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-600">
              {portfolioStructures.length}
            </span>
          </div>
          <span className="text-neutral-400">{structuresPanelOpen ? "▲" : "▼"}</span>
        </button>

        {structuresPanelOpen && (
          <div className="border-t border-neutral-200 p-5">
            <div className="grid gap-4 md:grid-cols-2">
              {portfolioStructures.map((structure) => {
                const structProps = committedProperties.filter(
                  (p) => p.structureId === structure.id
                );
                const propCount = structProps.length;
                // Use the canonical entity cashflow helper — same source
                // as the entity card pills below.
                const sTotalCashflow = computeEntityCashflow(structProps, structure, committedProperties, structure.id);
                const cashflowFn = sTotalCashflow * 12 / 26;

                return (
                  <div key={structure.id} className="rounded-2xl bg-white p-4 shadow-sm">
                    <div className="min-w-0">
                      <div className="font-semibold text-neutral-900 truncate">
                        {structure.displayName}
                      </div>
                      <span className="mt-1 inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                        {structure.entityType}
                      </span>
                      {structure.abn && (
                        <div className="mt-1 text-xs text-neutral-400">ABN {structure.abn}</div>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-xs text-neutral-400">Properties</div>
                        <div className="font-medium text-neutral-900">{propCount}</div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-400">Cashflow/fn</div>
                        <div className={`font-medium ${cashflowFn >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {cashflowFn >= 0 ? "+" : "-"}${Math.round(Math.abs(cashflowFn))}/fn
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-right">
              <button
                onClick={onNavigateToProfile}
                className="text-sm text-neutral-500 hover:text-neutral-700 hover:underline"
              >
                Manage structures in My Profile →
              </button>
            </div>
          </div>
        )}
      </div>

      {committedProperties.length > 0 && (() => {
        // Portfolio-wide totals derived from per-entity canonical cashflows
        // (Day 37 — number consistency rule). Money cannot flow across
        // entities, so top-ups must net per-entity then sum, NOT net across
        // the whole portfolio. Same formula as the entity card pills.
        const structureIds = new Set(portfolioStructures.map((s) => s.id));
        const unassignedProps = committedProperties.filter((p) => !structureIds.has(p.structureId));
        const allStatuses = committedProperties.map((p) => propertyHoldingStatus(p));
        const needingSupport = allStatuses.filter((h) => h.status !== "positive").length;

        let portfolioMonthlyCashflow = 0;
        let totalMonthlyTopUp = 0;
        for (const structure of portfolioStructures) {
          const sProps = committedProperties.filter((p) => p.structureId === structure.id);
          if (sProps.length === 0) continue;
          const sCashflow = computeEntityCashflow(sProps, structure, committedProperties, structure.id);
          const sPersonalTopUp = sProps.reduce((s, p) => s + (p.personalTopUp ?? 0) * ((p.ownershipPercent ?? 100) / 100), 0);
          // SMSF entities are funded by member contributions — exclude
          // from the personal top-up total and personal cashflow when toggle is OFF.
          if (includeSMSFInPortfolio || !isSMSF(structure.entityType)) {
            portfolioMonthlyCashflow += sCashflow;
            totalMonthlyTopUp += Math.max(0, -sCashflow) + sPersonalTopUp;
          }
        }
        if (unassignedProps.length > 0) {
          const uCashflow = unassignedProps.reduce(
            (s, p) => s + propertyMetrics(p).netCashflowShare,
            0
          );
          const uPersonalTopUp = unassignedProps.reduce((s, p) => s + (p.personalTopUp ?? 0) * ((p.ownershipPercent ?? 100) / 100), 0);
          portfolioMonthlyCashflow += uCashflow;
          totalMonthlyTopUp += Math.max(0, -uCashflow) + uPersonalTopUp;
        }
        const portfolioCashflowFn = portfolioMonthlyCashflow * 12 / 26;
        return (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <MiniMetric title="Total Properties" value={`${committedProperties.length}`} />
            <MiniMetric
              title="Portfolio Cashflow"
              value={`${portfolioCashflowFn >= 0 ? "+" : "-"}$${Math.round(Math.abs(portfolioCashflowFn))}/fn`}
              valueColour={portfolioCashflowFn >= 0 ? "text-emerald-700" : "text-red-600"}
              tip="Cash position. Rent minus all expenses minus full P&I loan repayments (interest + principal), share-adjusted, summed across entities. This is what actually leaves your bank account each fortnight. Negative gearing tax savings are not subtracted here — for the tax-position view see Strategy → Personal Tax Position or Reports → Net cashflow."
            />
            <MiniMetric
              title="Needing Support"
              value={`${needingSupport} of ${committedProperties.length}`}
              valueColour={needingSupport > 0 ? "text-red-600" : "text-emerald-700"}
            />
            <MiniMetric title="Monthly Top-Up" value={`${currency(totalMonthlyTopUp)}/mo`} />
          </div>
        );
      })()}

      {draftProperties.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-lg font-semibold text-amber-900">
            Draft Properties Added From Simulator
          </h2>
          <p className="mt-2 text-sm text-amber-800">
            These are scenario-based properties that have been added as draft portfolio entries.
            Review them here before confirming them as live holdings.
          </p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {draftProperties.map((property) => {
              const metrics = propertyMetrics(property);
              const draftStructure = portfolioStructures.find((s) => s.id === property.structureId);
              const draftDisplayCashflow = companyAdjustedCashflow(metrics.netCashflow, draftStructure?.entityType);
              const draftCompanyTax = isCompanyEntity(draftStructure?.entityType) && metrics.netCashflow > 0;
              return (
                <div key={property.id} className="rounded-2xl bg-white p-5">
                  <div className="text-sm text-neutral-500">Draft Scenario Property</div>
                  <div className="mt-1 text-lg font-semibold text-neutral-900">
                    {property.nickname}
                  </div>
                  <div className="text-sm text-neutral-500">{property.address}</div>

                  <div className="mt-4 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Purchase price</span>
                      <span className="font-medium text-neutral-900">{currency(property.purchasePrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Weekly rent</span>
                      <span className="font-medium text-neutral-900">{currency(property.weeklyRent)}/wk</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Loan balance</span>
                      <span className="font-medium text-neutral-900">{currency(totalLoanBalance(property))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Monthly expenses</span>
                      <span className="font-medium text-neutral-900">{currency(Math.round(property.monthlyExpenses * 100) / 100)}/mo</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Cashflow</span>
                      <span className={`font-medium ${draftDisplayCashflow >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {currency(draftDisplayCashflow)}/mo
                        {draftCompanyTax && <span className="text-xs font-normal text-neutral-400 ml-1">(after 30% company tax)</span>}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={() => onGoToSimulator?.()}
                      className="rounded-xl bg-neutral-900 px-4 py-2 text-sm text-white"
                    >
                      Edit in Simulator
                    </button>

                    <button
                      onClick={() => {
                        const estimatedCosts = (property.acquisitionCosts ?? []).reduce(
                          (sum, r) => sum + r.amount,
                          0
                        );
                        setConfirmModal({
                          propertyId: property.id,
                          nickname: property.nickname,
                          address: property.address,
                          purchasePrice: property.purchasePrice,
                          estimatedCosts,
                        });
                      }}
                      className="rounded-xl bg-green-600 px-4 py-2 text-sm text-white"
                    >
                      Confirm as Live
                    </button>

                    <button
                      onClick={() => onDeleteDraft?.(property.id)}
                      className="rounded-xl bg-neutral-200 px-4 py-2 text-sm text-neutral-900"
                    >
                      Delete Draft
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {portfolioStructures.map((structure) => {
        const structureProperties = committedProperties.filter(
          (p) => p.structureId === structure.id
        );

        if (structureProperties.length === 0) return null;

        // B27 #1 + #4: entity-level aggregates route through portfolioSummary()
        // SSOT. Value and debt are always gross (per ARCHITECTURE §7);
        // share-aware fields (totalCashflow, totalTopUp) are not used here
        // because this entity card derives its cashflow via computeEntityCashflow
        // (Day 37 single-source-of-truth for entity card).
        const entityTotals = portfolioSummary(structureProperties);
        const totalValue = entityTotals.totalValue;
        const totalDebt = entityTotals.totalDebt;
        const totalEquity = totalValue - totalDebt;
        const loanInterestFromOthers = interEntityLoanInterest(committedProperties, structure.id);

        // ─── CANONICAL ENTITY CASHFLOW (Day 37 — number consistency rule) ───
        // Single source of truth for all three pills on this card. Per
        // ARCHITECTURE §7 "Number consistency rule": top-up, self-funding
        // verdict, and fortnightly cashflow all derive from this one figure.
        // Pre-Day-37 each pill had its own independent formula and they
        // could disagree (e.g. Self-funding badge + non-zero top-up
        // simultaneously when positive properties offset negative ones).
        const totalCashflow = computeEntityCashflow(structureProperties, structure, committedProperties, structure.id);

        // Per-property personal top-up overrides — preserved additively.
        // Rare in practice (usually 0). If non-zero, the user is explicitly
        // contributing extra regardless of net position; in that edge case
        // the badge can show with a non-zero top-up — that's intentional.
        const totalPersonalTopUp = structureProperties.reduce(
          (sum, p) => sum + (p.personalTopUp ?? 0) * ((p.ownershipPercent ?? 100) / 100),
          0
        );
        // Top-up derived from totalCashflow — guaranteed to agree with
        // self-funding verdict. A net-positive entity has $0 top-up (modulo
        // any explicit personalTopUp overrides).
        const totalTopUp = Math.max(0, -totalCashflow) + totalPersonalTopUp;
        // Fortnightly = monthly × 12/26 from the same source figure.
        // No more independent reduce with different adjustments.
        const entityCashflowFn = totalCashflow * 12 / 26;

        const calculatedSelfFunding = totalCashflow > 0;
        const overrideValue = structure.selfFundingOverride;
        const effectiveSelfFunding = overrideValue ?? calculatedSelfFunding;
        const isOverridden = overrideValue !== null && overrideValue !== undefined;

        function setOverride(next: boolean | null) {
          if (!onUpdateStructure) return;
          onUpdateStructure({ ...structure, selfFundingOverride: next });
        }

        return (
          <div key={structure.id} className="rounded-2xl bg-neutral-50 p-4 md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold text-neutral-900">
                    {structure.displayName}
                  </h2>
                  {effectiveSelfFunding && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      Self-funding
                      {isOverridden && overrideValue === true && (
                        <span className="text-emerald-600/80">(override)</span>
                      )}
                      <InfoTooltip tip="Self-funding means rental income from this entity's properties covers all the loan interest, expenses and tax — you're not topping it up out of pocket. Use the override below if you want to mark this entity as self-funding manually (for example, you've prepaid the year's interest and the calculation hasn't caught up)." />
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-neutral-500">
                  {structure.entityType}
                </p>
                {/* Self-funding override segmented control — muted by default, only highlights when explicitly set */}
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-neutral-400">Self-funding:</span>
                  <div className="inline-flex rounded-md border border-neutral-200 bg-white p-0.5 text-xs">
                    {([
                      { val: null,  label: "Auto" },
                      { val: true,  label: "On" },
                      { val: false, label: "Off" },
                    ] as const).map(({ val, label }) => {
                      const selected =
                        (val === null && (overrideValue === null || overrideValue === undefined)) ||
                        (val === true && overrideValue === true) ||
                        (val === false && overrideValue === false);
                      const activeStyles =
                        val === null
                          ? "bg-neutral-100 text-neutral-700"
                          : val === true
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700";
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setOverride(val)}
                          disabled={!onUpdateStructure}
                          className={`px-2 py-0.5 rounded ${selected ? activeStyles : "text-neutral-400 hover:text-neutral-600"}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <InfoTooltip tip="Auto follows the calculated cashflow (rent − expenses − interest − tax). On forces the self-funding badge to show even if the numbers don't quite reach it — useful if you've prepaid interest or know something the calculation doesn't. Off hides the badge regardless. This is a label only — it doesn't change any tax, cashflow, or strategy numbers." />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                <MiniMetric title="Value" value={currency(totalValue)} />
                <MiniMetric title="Debt" value={currency(totalDebt)} />
                <MiniMetric title="Equity" value={currency(totalEquity)} />
                <MiniMetric title="Top-Up" value={currency(totalTopUp)} />
                <MiniMetric
                  title="Cashflow/fn"
                  value={`${entityCashflowFn >= 0 ? "+" : "-"}$${Math.round(Math.abs(entityCashflowFn))}/fn`}
                  valueColour={entityCashflowFn >= 0 ? "text-emerald-700" : "text-red-600"}
                  tip="Cash position for this entity. Rent minus expenses minus full P&I loan repayments (interest + principal), share-adjusted, summed across the entity's properties. Includes 30% company tax adjustment for Company entities and inter-entity loan interest deductions."
                />
              </div>
            </div>

            {/* SMSF fund position — only for SMSF entities */}
            {isSMSF(structure.entityType) && (() => {
              const contributions = structure.monthlyContributions ?? 0;
              const fundNetPosition = totalCashflow + contributions;
              return (
                <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Fund position</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">SMSF</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-neutral-400">Property cashflow</div>
                      <div className={`font-medium ${totalCashflow >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {totalCashflow >= 0 ? "+" : ""}{currency(Math.round(totalCashflow))}/mo
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-400">Contributions</div>
                      <div className="font-medium text-neutral-900">
                        +{currency(Math.round(contributions))}/mo
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-400">Net fund position</div>
                      <div className={`font-semibold ${fundNetPosition >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {fundNetPosition >= 0 ? "+" : ""}{currency(Math.round(fundNetPosition))}/mo
                        <span className="text-xs font-normal ml-1">{fundNetPosition >= 0 ? "surplus" : "deficit"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="flex items-center gap-1 text-xs font-medium text-neutral-500">
                      Monthly contributions ($)
                      <InfoTooltip tip="Enter the total monthly contributions flowing into the fund — member salary sacrifice + employer superannuation guarantee (SG) + any non-concessional contributions. This offsets the fund's property expenses and is excluded from your personal top-up calculation. If this fund holds property via an LRBA (Limited Recourse Borrowing Arrangement), loan repayments come from the fund's rental income and contributions — not from your personal cashflow." />
                    </label>
                    <NumberInput
                      className="mt-1 w-40 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="0"
                      value={contributions || ""}
                      onChange={(e) => {
                        if (!onUpdateStructure) return;
                        onUpdateStructure({ ...structure, monthlyContributions: parseFloat(e.target.value) || 0 });
                      }}
                    />
                  </div>
                  <p className="mt-2 text-[11px] text-neutral-400 italic">
                    SMSF cashflow is funded by member and employer contributions — excluded from personal top-up calculations.
                  </p>
                </div>
              );
            })()}

            {/* Mobile property cards — md:hidden */}
            <div className="mt-6 space-y-3 md:hidden">
              {structureProperties.map((property) => {
                const metrics = propertyMetrics(property);
                const displayCashflow = companyAdjustedCashflow(metrics.netCashflow, structure.entityType);
                const annualExpenses = (property.annualRates ?? 0) + (property.annualInsurance ?? 0) + (property.annualStrata ?? 0) + (property.annualLandTax ?? 0) + (property.annualMaintenance ?? 0) + (property.annualPropertyManagement ?? 0);
                const gYield = grossYield(property.weeklyRent ?? 0, property.currentValue ?? 0);
                const nYield = netYield(property.weeklyRent ?? 0, property.currentValue ?? 0, annualExpenses);
                const yieldColor = gYield >= 5 ? "text-emerald-700" : gYield >= 3 ? "text-amber-600" : "text-red-600";
                return (
                  <div
                    key={property.id}
                    onClick={() => onSelectProperty?.(property.id)}
                    className="cursor-pointer rounded-xl bg-white px-4 py-3.5 shadow-sm active:bg-neutral-50"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-neutral-900 truncate">{property.nickname}</div>
                        <div className="text-xs text-neutral-500 truncate">{property.address}</div>
                      </div>
                      <span className="shrink-0 text-sm text-neutral-400">View →</span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-xs text-neutral-400">Value</div>
                        <div className="text-sm font-medium text-neutral-900">{currency(property.currentValue)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-400">Cashflow</div>
                        <div className={`text-sm font-medium ${displayCashflow >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {currency(displayCashflow)}/mo
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-400">LVR</div>
                        <div className="text-sm font-medium text-neutral-900">{percent(metrics.lvr)}</div>
                      </div>
                    </div>
                    {gYield > 0 && (
                      <div className="mt-2 border-t border-neutral-100 pt-2">
                        <div className="text-xs text-neutral-400">Yield</div>
                        <div className={`text-sm font-medium ${yieldColor}`}>
                          {gYield.toFixed(1)}% gross / {nYield.toFixed(1)}% net
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop table — hidden on mobile */}
            <div className="mt-6 hidden overflow-x-auto md:block">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 text-left text-neutral-500">
                    <th className="pb-3 pr-4 font-medium">Property</th>
                    <th className="pb-3 pr-4 font-medium">Value</th>
                    <th className="pb-3 pr-4 font-medium">Debt</th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Equity<InfoTooltip tip="Current value minus outstanding loan balance for this property." /></span></th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Cashflow/mo<InfoTooltip tip="Cash position. Monthly rent minus expenses minus full P&I loan repayments (interest + principal). Gross figure shown — co-ownership share is applied at portfolio aggregates, not per-property displays." /></span></th>
                    <th className="pb-3 pr-4 font-medium">Top-Up</th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">LVR<InfoTooltip tip="Loan balance divided by current value for this property. Lower is better." /></span></th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Yield<InfoTooltip tip="Gross yield = (weekly rent × 52) ÷ current value. Net yield also deducts annual expenses." /></span></th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Cashflow/fn<InfoTooltip tip="Cash position fortnightly. Same as Cashflow column converted to /fn (× 12 / 26). Property is flagged 'needs top-up' when share-adjusted cashflow is negative — uses the same Path A semantic as Portfolio Cashflow." /></span></th>
                  </tr>
                </thead>
                <tbody>
                  {structureProperties.map((property) => {
                    const metrics = propertyMetrics(property);
                    const holding = propertyHoldingStatus(property);
                    const displayCashflow = companyAdjustedCashflow(metrics.netCashflow, structure.entityType);
                    const companyTaxApplied = isCompanyEntity(structure.entityType) && metrics.netCashflow > 0;
                    const annualExpenses = (property.annualRates ?? 0) + (property.annualInsurance ?? 0) + (property.annualStrata ?? 0) + (property.annualLandTax ?? 0) + (property.annualMaintenance ?? 0) + (property.annualPropertyManagement ?? 0);
                    const gYield = grossYield(property.weeklyRent ?? 0, property.currentValue ?? 0);
                    const yieldColor = gYield >= 5 ? "text-emerald-700" : gYield >= 3 ? "text-amber-600" : "text-red-600";

                    return (
                      <tr
                        key={property.id}
                        onClick={() => onSelectProperty?.(property.id)}
                        className="border-b border-neutral-100 last:border-0 cursor-pointer hover:bg-neutral-100 transition-colors"
                      >
                        <td className="py-4 pr-4">
                          <div className="font-medium text-neutral-900">
                            {property.nickname}
                          </div>
                          <div className="text-neutral-500">
                            {property.address}
                          </div>
                        </td>
                        <td className="py-4 pr-4">{currency(property.currentValue)}</td>
                        <td className="py-4 pr-4">{currency(totalLoanBalance(property))}</td>
                        <td className="py-4 pr-4">{currency(metrics.equity)}</td>
                        <td
                          className={`py-4 pr-4 font-medium ${
                            displayCashflow >= 0
                              ? "text-emerald-700"
                              : "text-red-600"
                          }`}
                        >
                          {currency(displayCashflow)}<span className="text-xs font-normal text-neutral-400"> /mo</span>
                          {companyTaxApplied && (
                            <div className="text-xs font-normal text-neutral-400">(after 30% company tax)</div>
                          )}
                        </td>
                        <td className="py-4 pr-4">{currency(metrics.requiredTopUp)}</td>
                        <td className="py-4 pr-4">{percent(metrics.lvr)}</td>
                        <td className={`py-4 pr-4 font-medium ${gYield > 0 ? yieldColor : "text-neutral-400"}`}>
                          {gYield > 0 ? (
                            <>
                              {gYield.toFixed(1)}%
                              <div className="text-xs font-normal text-neutral-400">{netYield(property.weeklyRent ?? 0, property.currentValue ?? 0, annualExpenses).toFixed(1)}% net</div>
                            </>
                          ) : "—"}
                        </td>
                        <td className="py-4 pr-4">
                          {holding.status === "positive" ? (
                            <span className="inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                              +${Math.round(holding.netFortnightly)}/fn
                            </span>
                          ) : (
                            <span className="inline-block rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                              -${Math.round(Math.abs(holding.netFortnightly))}/fn · needs top-up
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  <tr className="border-t-2 border-neutral-300 font-semibold text-neutral-900">
                    <td className="py-4 pr-4">Entity Total</td>
                    <td className="py-4 pr-4">{currency(totalValue)}</td>
                    <td className="py-4 pr-4">{currency(totalDebt)}</td>
                    <td className="py-4 pr-4">{currency(totalEquity)}</td>
                    <td
                      className={`py-4 pr-4 ${
                        totalCashflow >= 0 ? "text-emerald-700" : "text-red-600"
                      }`}
                    >
                      {currency(totalCashflow)}
                      {isCompanyEntity(structure.entityType) && totalCashflow > 0 && (
                        <div className="text-xs font-normal text-neutral-400">(after 30% company tax)</div>
                      )}
                      {loanInterestFromOthers > 0 && (
                        <div className="text-xs font-normal text-neutral-400">(incl. inter-entity loan interest)</div>
                      )}
                    </td>
                    <td className="py-4 pr-4">{currency(totalTopUp)}</td>
                    <td className="py-4 pr-4">—</td>
                    <td className="py-4 pr-4">—</td>
                    <td className={`py-4 pr-4 ${entityCashflowFn >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {entityCashflowFn >= 0 ? "+" : "-"}${Math.round(Math.abs(entityCashflowFn))}/fn
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {/* ── Personal-name properties group (B111) ────────────────────────────
          Properties held in personal name(s) have structure_id IS NULL — that
          is the SSOT (B106 / L40). Render them as a normal entity-style group
          rather than a yellow "Unassigned" warning. No company-tax adjustment
          (Personal is not a Company entity); no SMSF fund block; no self-
          funding override control (override is a structure-level field). */}
      {(() => {
        const personalProperties = committedProperties.filter((p) => !p.structureId);
        if (personalProperties.length === 0) return null;

        const personalTotals = portfolioSummary(personalProperties);
        const totalValue = personalTotals.totalValue;
        const totalDebt = personalTotals.totalDebt;
        const totalEquity = totalValue - totalDebt;
        const totalCashflow = personalProperties.reduce(
          (sum, p) => sum + propertyMetrics(p).netCashflowShare,
          0
        );
        const totalPersonalTopUp = personalProperties.reduce(
          (sum, p) => sum + (p.personalTopUp ?? 0) * ((p.ownershipPercent ?? 100) / 100),
          0
        );
        const totalTopUp = Math.max(0, -totalCashflow) + totalPersonalTopUp;
        const entityCashflowFn = totalCashflow * 12 / 26;
        const calculatedSelfFunding = totalCashflow > 0;

        return (
          <div className="rounded-2xl bg-neutral-50 p-4 md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold text-neutral-900">Personal</h2>
                  {calculatedSelfFunding && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      Self-funding
                      <InfoTooltip tip="Self-funding means rental income from these personal-name properties covers all the loan interest, expenses and tax — you're not topping up out of pocket." />
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-neutral-500">
                  Held in personal name(s)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                <MiniMetric title="Value" value={currency(totalValue)} />
                <MiniMetric title="Debt" value={currency(totalDebt)} />
                <MiniMetric title="Equity" value={currency(totalEquity)} />
                <MiniMetric title="Top-Up" value={currency(totalTopUp)} />
                <MiniMetric
                  title="Cashflow/fn"
                  value={`${entityCashflowFn >= 0 ? "+" : "-"}$${Math.round(Math.abs(entityCashflowFn))}/fn`}
                  valueColour={entityCashflowFn >= 0 ? "text-emerald-700" : "text-red-600"}
                  tip="Cash position for personal-name properties. Rent minus expenses minus full P&I loan repayments (interest + principal), share-adjusted, summed across the personal properties."
                />
              </div>
            </div>

            {/* Mobile property cards — md:hidden */}
            <div className="mt-6 space-y-3 md:hidden">
              {personalProperties.map((property) => {
                const metrics = propertyMetrics(property);
                const displayCashflow = metrics.netCashflow;
                const annualExpenses = (property.annualRates ?? 0) + (property.annualInsurance ?? 0) + (property.annualStrata ?? 0) + (property.annualLandTax ?? 0) + (property.annualMaintenance ?? 0) + (property.annualPropertyManagement ?? 0);
                const gYield = grossYield(property.weeklyRent ?? 0, property.currentValue ?? 0);
                const nYield = netYield(property.weeklyRent ?? 0, property.currentValue ?? 0, annualExpenses);
                const yieldColor = gYield >= 5 ? "text-emerald-700" : gYield >= 3 ? "text-amber-600" : "text-red-600";
                return (
                  <div
                    key={property.id}
                    onClick={() => onSelectProperty?.(property.id)}
                    className="cursor-pointer rounded-xl bg-white px-4 py-3.5 shadow-sm active:bg-neutral-50"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-neutral-900 truncate">{property.nickname}</div>
                        <div className="text-xs text-neutral-500 truncate">{property.address}</div>
                      </div>
                      <span className="shrink-0 text-sm text-neutral-400">View →</span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-xs text-neutral-400">Value</div>
                        <div className="text-sm font-medium text-neutral-900">{currency(property.currentValue)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-400">Cashflow</div>
                        <div className={`text-sm font-medium ${displayCashflow >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {currency(displayCashflow)}/mo
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-400">LVR</div>
                        <div className="text-sm font-medium text-neutral-900">{percent(metrics.lvr)}</div>
                      </div>
                    </div>
                    {gYield > 0 && (
                      <div className="mt-2 border-t border-neutral-100 pt-2">
                        <div className="text-xs text-neutral-400">Yield</div>
                        <div className={`text-sm font-medium ${yieldColor}`}>
                          {gYield.toFixed(1)}% gross / {nYield.toFixed(1)}% net
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop table — hidden on mobile */}
            <div className="mt-6 hidden overflow-x-auto md:block">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 text-left text-neutral-500">
                    <th className="pb-3 pr-4 font-medium">Property</th>
                    <th className="pb-3 pr-4 font-medium">Value</th>
                    <th className="pb-3 pr-4 font-medium">Debt</th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Equity<InfoTooltip tip="Current value minus outstanding loan balance for this property." /></span></th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Cashflow/mo<InfoTooltip tip="Cash position. Monthly rent minus expenses minus full P&I loan repayments (interest + principal). Gross figure shown — co-ownership share is applied at portfolio aggregates, not per-property displays." /></span></th>
                    <th className="pb-3 pr-4 font-medium">Top-Up</th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">LVR<InfoTooltip tip="Loan balance divided by current value for this property. Lower is better." /></span></th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Yield<InfoTooltip tip="Gross yield = (weekly rent × 52) ÷ current value. Net yield also deducts annual expenses." /></span></th>
                    <th className="pb-3 pr-4 font-medium"><span className="flex items-center gap-1">Cashflow/fn<InfoTooltip tip="Cash position fortnightly. Same as Cashflow column converted to /fn (× 12 / 26). Property is flagged 'needs top-up' when share-adjusted cashflow is negative — uses the same Path A semantic as Portfolio Cashflow." /></span></th>
                  </tr>
                </thead>
                <tbody>
                  {personalProperties.map((property) => {
                    const metrics = propertyMetrics(property);
                    const holding = propertyHoldingStatus(property);
                    const displayCashflow = metrics.netCashflow;
                    const annualExpenses = (property.annualRates ?? 0) + (property.annualInsurance ?? 0) + (property.annualStrata ?? 0) + (property.annualLandTax ?? 0) + (property.annualMaintenance ?? 0) + (property.annualPropertyManagement ?? 0);
                    const gYield = grossYield(property.weeklyRent ?? 0, property.currentValue ?? 0);
                    const yieldColor = gYield >= 5 ? "text-emerald-700" : gYield >= 3 ? "text-amber-600" : "text-red-600";

                    return (
                      <tr
                        key={property.id}
                        onClick={() => onSelectProperty?.(property.id)}
                        className="border-b border-neutral-100 last:border-0 cursor-pointer hover:bg-neutral-100 transition-colors"
                      >
                        <td className="py-4 pr-4">
                          <div className="font-medium text-neutral-900">
                            {property.nickname}
                          </div>
                          <div className="text-neutral-500">
                            {property.address}
                          </div>
                        </td>
                        <td className="py-4 pr-4">{currency(property.currentValue)}</td>
                        <td className="py-4 pr-4">{currency(totalLoanBalance(property))}</td>
                        <td className="py-4 pr-4">{currency(metrics.equity)}</td>
                        <td
                          className={`py-4 pr-4 font-medium ${
                            displayCashflow >= 0
                              ? "text-emerald-700"
                              : "text-red-600"
                          }`}
                        >
                          {currency(displayCashflow)}<span className="text-xs font-normal text-neutral-400"> /mo</span>
                        </td>
                        <td className="py-4 pr-4">{currency(metrics.requiredTopUp)}</td>
                        <td className="py-4 pr-4">{percent(metrics.lvr)}</td>
                        <td className={`py-4 pr-4 font-medium ${gYield > 0 ? yieldColor : "text-neutral-400"}`}>
                          {gYield > 0 ? (
                            <>
                              {gYield.toFixed(1)}%
                              <div className="text-xs font-normal text-neutral-400">{netYield(property.weeklyRent ?? 0, property.currentValue ?? 0, annualExpenses).toFixed(1)}% net</div>
                            </>
                          ) : "—"}
                        </td>
                        <td className="py-4 pr-4">
                          {holding.status === "positive" ? (
                            <span className="inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                              +${Math.round(holding.netFortnightly)}/fn
                            </span>
                          ) : (
                            <span className="inline-block rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                              -${Math.round(Math.abs(holding.netFortnightly))}/fn · needs top-up
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  <tr className="border-t-2 border-neutral-300 font-semibold text-neutral-900">
                    <td className="py-4 pr-4">Personal Total</td>
                    <td className="py-4 pr-4">{currency(totalValue)}</td>
                    <td className="py-4 pr-4">{currency(totalDebt)}</td>
                    <td className="py-4 pr-4">{currency(totalEquity)}</td>
                    <td
                      className={`py-4 pr-4 ${
                        totalCashflow >= 0 ? "text-emerald-700" : "text-red-600"
                      }`}
                    >
                      {currency(totalCashflow)}
                    </td>
                    <td className="py-4 pr-4">{currency(totalTopUp)}</td>
                    <td className="py-4 pr-4">—</td>
                    <td className="py-4 pr-4">—</td>
                    <td className={`py-4 pr-4 ${entityCashflowFn >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {entityCashflowFn >= 0 ? "+" : "-"}${Math.round(Math.abs(entityCashflowFn))}/fn
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      </>)}

      {portfolioTab === "history" && (
        <div className="space-y-4">
          {historicalProperties.length === 0 ? (
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-6 py-12 text-center">
              <div className="text-2xl mb-2">🏚</div>
              <div className="text-sm font-medium text-neutral-700">No sold or historical properties yet</div>
              <div className="mt-1 text-xs text-neutral-500">
                When you mark a property as sold from its detail page, it will appear here.
              </div>
            </div>
          ) : (
            <>
              {historicalProperties.map((property) => {
                const costBase = (property.purchasePrice ?? 0) + (property.buyCosts ?? 0) + (property.legalBuyCosts ?? 0)
                  + (property.capitalImprovements ?? []).reduce((s, ci) => s + ci.amount, 0)
                  - (property.depreciationClaimed ?? []).reduce((s, d) => s + d.amountClaimed, 0);
                const saleRevenue = property.salePrice ?? 0;
                const saleCosts = (property.saleAgentFees ?? 0) + (property.saleLegalCosts ?? 0) + (property.saleOtherCosts ?? 0);
                const grossProfit = saleRevenue - costBase - saleCosts;
                const structure = portfolioStructures.find((s) => s.id === property.structureId);

                return (
                  <div key={property.id} className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-neutral-900">{property.nickname}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            property.status === "sold" ? "bg-red-100 text-red-700" : "bg-neutral-100 text-neutral-600"
                          }`}>
                            {property.status === "sold" ? "Sold" : "Historical"}
                          </span>
                        </div>
                        <div className="mt-0.5 text-sm text-neutral-500">{property.address}</div>
                        {structure && (
                          <div className="mt-1 text-xs text-neutral-400">{structure.displayName} · {structure.entityType}</div>
                        )}
                      </div>
                      <button
                        onClick={() => onSelectProperty?.(property.id)}
                        className="shrink-0 rounded-xl border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
                      >
                        View detail
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <div className="rounded-xl bg-neutral-50 px-3 py-2.5">
                        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Purchase price</div>
                        <div className="mt-1 text-sm font-semibold text-neutral-900">{currency(property.purchasePrice)}</div>
                      </div>
                      <div className="rounded-xl bg-neutral-50 px-3 py-2.5">
                        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Sale price</div>
                        <div className="mt-1 text-sm font-semibold text-neutral-900">{property.salePrice ? currency(property.salePrice) : "—"}</div>
                      </div>
                      <div className="rounded-xl bg-neutral-50 px-3 py-2.5">
                        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Sale costs</div>
                        <div className="mt-1 text-sm font-semibold text-neutral-900">{saleCosts > 0 ? currency(saleCosts) : "—"}</div>
                      </div>
                      <div className={`rounded-xl px-3 py-2.5 ${saleRevenue > 0 ? (grossProfit >= 0 ? "bg-emerald-50" : "bg-red-50") : "bg-neutral-50"}`}>
                        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Gross profit</div>
                        <div className={`mt-1 text-sm font-semibold ${saleRevenue > 0 ? (grossProfit >= 0 ? "text-emerald-700" : "text-red-600") : "text-neutral-400"}`}>
                          {saleRevenue > 0 ? (grossProfit >= 0 ? "+" : "") + currency(grossProfit) : "—"}
                        </div>
                      </div>
                    </div>

                    {property.saleDate && (
                      <div className="mt-3 text-xs text-neutral-400">
                        Sale date: {new Date(property.saleDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                        {property.saleNotes && <> · {property.saleNotes}</>}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-5 py-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
                  <div>
                    <div className="text-xs text-neutral-400 uppercase tracking-widest">Properties sold</div>
                    <div className="mt-1 font-semibold text-neutral-900">{historicalProperties.length}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400 uppercase tracking-widest">Total sale proceeds</div>
                    <div className="mt-1 font-semibold text-neutral-900">
                      {currency(historicalProperties.reduce((s, p) => s + (p.salePrice ?? 0), 0))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400 uppercase tracking-widest">Total purchase price</div>
                    <div className="mt-1 font-semibold text-neutral-900">
                      {currency(historicalProperties.reduce((s, p) => s + (p.purchasePrice ?? 0), 0))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400 uppercase tracking-widest">Total gross profit</div>
                    {(() => {
                      const totalProfit = historicalProperties.reduce((s, p) => {
                        const cb = (p.purchasePrice ?? 0) + (p.buyCosts ?? 0) + (p.legalBuyCosts ?? 0)
                          + (p.capitalImprovements ?? []).reduce((x, ci) => x + ci.amount, 0)
                          - (p.depreciationClaimed ?? []).reduce((x, d) => x + d.amountClaimed, 0);
                        const costs = (p.saleAgentFees ?? 0) + (p.saleLegalCosts ?? 0) + (p.saleOtherCosts ?? 0);
                        return s + (p.salePrice ?? 0) - cb - costs;
                      }, 0);
                      return (
                        <div className={`mt-1 font-semibold ${totalProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {totalProfit >= 0 ? "+" : ""}{currency(totalProfit)}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-neutral-900">
              Confirm {confirmModal.nickname} as a live property
            </h2>
            <p className="mt-1 text-sm text-neutral-500">{confirmModal.address}</p>

            <div className="mt-4 space-y-1 rounded-xl bg-neutral-50 px-4 py-3 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500">Purchase price</span>
                <span className="font-medium text-neutral-900">{currency(confirmModal.purchasePrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Acquisition costs</span>
                <span className="font-medium text-neutral-900">
                  {currency(confirmModal.estimatedCosts)}{" "}
                  <span className="font-normal text-neutral-400">(estimated from simulator)</span>
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-sm font-medium text-neutral-900">Use simulator estimates</div>
                <div className="mt-1 text-xs text-neutral-500">
                  Confirm with the figures already imported from the simulator.
                </div>
                <button
                  onClick={() => {
                    onConfirmDraft?.(confirmModal.propertyId);
                    setConfirmModal(null);
                  }}
                  className="mt-3 rounded-xl bg-green-600 px-4 py-2 text-sm text-white"
                >
                  Confirm with estimates
                </button>
              </div>

              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-sm font-medium text-neutral-900">I have the actual settlement figures</div>
                <div className="mt-1 text-xs text-neutral-500">
                  Confirm the property and go straight to the Acquisition Costs section to enter real figures.
                </div>
                <button
                  onClick={() => {
                    onConfirmDraft?.(confirmModal.propertyId);
                    onSelectProperty?.(confirmModal.propertyId);
                    setConfirmModal(null);
                  }}
                  className="mt-3 rounded-xl bg-neutral-900 px-4 py-2 text-sm text-white"
                >
                  Enter actual costs
                </button>
              </div>
            </div>

            <button
              onClick={() => setConfirmModal(null)}
              className="mt-4 w-full rounded-xl bg-neutral-100 px-4 py-2 text-sm text-neutral-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniMetric({ title, value, valueColour, tip }: { title: string; value: string; valueColour?: string; tip?: string }) {
  return (
    <div className="rounded-xl bg-white px-3 py-2.5 md:px-4 md:py-3">
      <div className="flex items-center gap-1 text-xs uppercase tracking-[0.12em] text-neutral-500">
        {title}
        {tip && <InfoTooltip tip={tip} />}
      </div>
      <div className={`mt-1 text-lg font-semibold ${valueColour ?? "text-neutral-900"}`}>{value}</div>
    </div>
  );
}